import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

const APP_URL = process.env.REACT_APP_BACKEND_URL || "";
const BRAND = "Global Lead Suite";
const JSON_LD_ID = "glms-seo-jsonld-graph";

// -- Idempotent tag helpers --
//
// Each helper finds the FIRST matching tag (regardless of whether it was
// stamped by setupProxy.js SSR, previous React render, or the static
// public/index.html) and updates its content — creating a new tag only
// if none exists. This means <SEO/> can safely run on every route change
// under React StrictMode (which double-invokes effects) without ever
// producing duplicates.

function setMeta(attr, key, content) {
    if (content == null || content === "") return;
    const selector = `meta[${attr}="${CSS.escape(key)}"]`;
    let tag = document.head.querySelector(selector);
    if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attr, key);
        document.head.appendChild(tag);
    }
    tag.setAttribute("content", content);
}

function setLink(rel, href) {
    if (!href) return;
    const selector = `link[rel="${rel}"]`;
    let tag = document.head.querySelector(selector);
    if (!tag) {
        tag = document.createElement("link");
        tag.setAttribute("rel", rel);
        document.head.appendChild(tag);
    }
    tag.setAttribute("href", href);
}

function setArticleTags(tags) {
    // Remove any existing article:tag meta (we always fully rewrite).
    document.head
        .querySelectorAll('meta[property="article:tag"]')
        .forEach((el) => el.remove());
    (tags || []).forEach((t) => {
        const m = document.createElement("meta");
        m.setAttribute("property", "article:tag");
        m.setAttribute("content", t);
        document.head.appendChild(m);
    });
}

function setHreflangs(entries) {
    document.head
        .querySelectorAll('link[rel="alternate"][hreflang]')
        .forEach((el) => el.remove());
    (entries || []).forEach((e) => {
        const l = document.createElement("link");
        l.setAttribute("rel", "alternate");
        l.setAttribute("hreflang", e.lang);
        l.setAttribute("href", e.href);
        document.head.appendChild(l);
    });
}

function setJsonLd(graph) {
    // Consolidate everything under ONE script tag with a stable id, so
    // we simply update its textContent on each render (no duplicates).
    let tag = document.getElementById(JSON_LD_ID);
    if (!tag) {
        tag = document.createElement("script");
        tag.id = JSON_LD_ID;
        tag.type = "application/ld+json";
        document.head.appendChild(tag);
    }
    tag.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@graph": graph,
    });
    // Also drop any legacy JSON-LD scripts left over from SSR that used
    // a different tag; the SSR block emits one per @graph entry. On
    // client-side updates we consolidate them into ours.
    document.head
        .querySelectorAll('script[type="application/ld+json"]')
        .forEach((s) => { if (s.id !== JSON_LD_ID) s.remove(); });
}

function brandTitle(pageTitle) {
    if (!pageTitle) return `${BRAND} — AI Lead Generation`;
    // Mirror seo_ssr_service._envelope(): only append the brand suffix
    // when the title doesn't already contain it. Prevents client-side
    // double-suffix like "Global Lead Suite Blog · Global Lead Suite".
    return pageTitle.includes(BRAND)
        ? pageTitle
        : `${pageTitle} · ${BRAND}`;
}

// Locale route prefixes handled by the SSR middleware. Kept in sync with
// setupProxy.js parseLocale() and App.js locale route enumeration.
const LOCALE_PREFIX_RE = /^\/(it|es|fr|de)(\/|$)/;

/**
 * Compute the canonical URL to emit.
 *
 * Page components pass hardcoded EN paths (e.g. `canonical="/pricing"`)
 * because they don't know the current locale prefix. If the browser URL
 * carries a locale prefix (`/it/pricing`), we must NOT overwrite the
 * SSR-emitted `/it/pricing` canonical with `/pricing` — that would
 * collapse all locale variants to EN in the eyes of JS-executing
 * crawlers. So: when the current URL is locale-prefixed, prefer
 * `location.pathname` over the caller-supplied relative canonical.
 */
function resolveCanonical(propValue, pathname) {
    if (propValue && propValue.startsWith("http")) return propValue;
    if (LOCALE_PREFIX_RE.test(pathname || "")) {
        return `${APP_URL}${pathname}`;
    }
    if (propValue) return `${APP_URL}${propValue}`;
    return `${APP_URL}${pathname || "/"}`;
}

/**
 * <SEO /> — idempotent document.head manager.
 *
 * Design:
 *   - No latching, no ssr-vs-client branching. Every effect run finds
 *     matching tags via querySelector and UPDATES them in place. A tag
 *     is created only if it doesn't exist. This is safe under React
 *     StrictMode's double-invoke because updates are the same value.
 *   - setupProxy.js SSR stamps the initial page head with real SEO;
 *     when React hydrates, this component finds those exact tags and
 *     writes the SAME values into them. No duplicate created.
 *   - On client-side navigation, the same effect runs on the new route
 *     with new inputs → the existing tags are updated to the new route's
 *     values. No stale canonical, no stale JSON-LD.
 */
export default function SEO({
    title,
    description,
    canonical,
    image,
    type = "website",
    jsonLd,
    noindex = false,
    locale,
    article = null,
}) {
    const location = useLocation();
    const { i18n } = useTranslation();
    const lang = locale || i18n?.language || "en";

    const url = resolveCanonical(canonical, location.pathname);
    const finalImage = image || `${APP_URL}/og-default.png`;
    const finalTitle = brandTitle(title);

    useEffect(() => {
        document.title = finalTitle;
        document.documentElement.setAttribute("lang", lang);
        // Clear the SSR marker once React has taken over — from now on
        // <SEO/> owns the head and keeps everything in sync on every
        // route change.
        document.documentElement.removeAttribute("data-glms-ssr");

        setMeta("name", "description", description);
        setMeta("name", "robots",
            noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large");
        setMeta("name", "geo.region", "EU");
        setMeta("name", "geo.placename", "European Union");
        setLink("canonical", url);

        setMeta("property", "og:type", type);
        setMeta("property", "og:site_name", BRAND);
        setMeta("property", "og:title", title || BRAND);
        setMeta("property", "og:description", description);
        setMeta("property", "og:url", url);
        setMeta("property", "og:image", finalImage);
        setMeta("property", "og:locale", lang.replace("-", "_"));

        setMeta("name", "twitter:card", "summary_large_image");
        setMeta("name", "twitter:title", title || BRAND);
        setMeta("name", "twitter:description", description);
        setMeta("name", "twitter:image", finalImage);

        if (article?.publishedTime)
            setMeta("property", "article:published_time", article.publishedTime);
        if (article?.modifiedTime)
            setMeta("property", "article:modified_time", article.modifiedTime);
        if (article?.author)
            setMeta("property", "article:author", article.author);
        setArticleTags(article?.tags);

        // Base graph is always Organization + WebSite; extras come from
        // the route (SoftwareApplication on /, BlogPosting on posts, ...).
        const baseGraph = [
            {
                "@type": "Organization",
                name: BRAND,
                url: APP_URL,
                logo: `${APP_URL}/logo.png`,
                sameAs: [],
            },
            {
                "@type": "WebSite",
                name: BRAND,
                url: APP_URL,
                potentialAction: {
                    "@type": "SearchAction",
                    target: `${APP_URL}/blog?q={search_term_string}`,
                    "query-input": "required name=search_term_string",
                },
            },
        ];
        const extras = jsonLd
            ? Array.isArray(jsonLd) ? jsonLd : [jsonLd]
            : [];
        setJsonLd([...baseGraph, ...extras]);

        setHreflangs([]);  // route-specific hreflangs are rendered by SSR;
        // client-side navigation between locales isn't supported in the
        // current UX (each locale has its own /it/, /es/ prefix that
        // triggers a full SSR fetch), so we don't try to keep hreflang
        // in sync on the client — that would require plumbing the
        // envelope down here too.
    }, [
        finalTitle, description, url, finalImage, type, noindex, lang,
        JSON.stringify(article || null), JSON.stringify(jsonLd || null),
        title,
    ]);

    return null;
}
